import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ndaFormSchema, checkRateLimit, getRateLimitResetTime } from '@/utils/formValidation';
import { ZodError } from 'zod';
import { useAnalytics } from '@/hooks/useAnalytics';

interface NDAModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  listingId: string;
}

export function NDAModal({ isOpen, onClose, onAccept, listingId }: NDAModalProps) {
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { trackNDASign } = useAnalytics();

  // Check authentication status
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async () => {
    setErrors({})

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to sign the NDA.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit (3 NDA signatures per hour per user)
    const rateLimitKey = `nda-${user.id}-${listingId}`
    if (!checkRateLimit(rateLimitKey, 3, 3600000)) {
      const resetTime = getRateLimitResetTime(rateLimitKey)
      const minutesLeft = resetTime ? Math.ceil(resetTime / 60000) : 60
      toast({
        title: "Too Many Requests",
        description: `Please wait ${minutesLeft} minutes before attempting again.`,
        variant: "destructive"
      })
      return
    }

    // Validate form data
    try {
      const validatedData = ndaFormSchema.parse({
        buyerName,
        buyerEmail,
        hasAgreed
      })

      setIsSubmitting(true);
    
      const { error } = await supabase
        .from('nda_signatures')
        .insert({
          listing_id: listingId,
          user_id: user.id,
          buyer_email: validatedData.buyerEmail,
          buyer_name: validatedData.buyerName,
          ip_address: null // Could be populated via edge function if needed
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "NDA already signed",
            description: "You have already signed the NDA for this listing.",
          });
        } else {
          throw error;
        }
      } else {
        trackNDASign(listingId);
        toast({
          title: "NDA signed successfully",
          description: "You can now view the confidential business information.",
        });
      }

      onAccept();
      onClose();
    } catch (error) {
      if (error instanceof ZodError) {
        // Handle validation errors
        const fieldErrors: Record<string, string> = {}
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0].toString()] = err.message
          }
        })
        setErrors(fieldErrors)
        toast({
          title: "Validation Error",
          description: "Please check the form for errors.",
          variant: "destructive"
        })
      } else {
        console.error('Error signing NDA:', error);
        toast({
          title: "Error signing NDA",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewNDA = () => {
    window.open('/documents/BizMatch_NDA.pdf', '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Non-Disclosure Agreement Required</DialogTitle>
          <DialogDescription>
            This business listing requires signing a Non-Disclosure Agreement before accessing confidential information.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-2">What is an NDA?</h3>
            <p className="text-sm text-muted-foreground">
              A Non-Disclosure Agreement (NDA) is a legal contract that protects confidential business information. 
              By signing this NDA, you agree to keep all shared business details confidential and use them only 
              for evaluating this potential acquisition.
            </p>
          </div>

          <div className="flex justify-center">
            <Button variant="outline" onClick={handleViewNDA}>
              View Full NDA Document
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="buyerName">Full Name *</Label>
              <Input
                id="buyerName"
                value={buyerName}
                onChange={(e) => {
                  setBuyerName(e.target.value)
                  if (errors.buyerName) setErrors(prev => ({ ...prev, buyerName: '' }))
                }}
                placeholder="Enter your full legal name"
                required
                maxLength={100}
                className={errors.buyerName ? 'border-destructive' : ''}
              />
              {errors.buyerName && <p className="text-sm text-destructive mt-1">{errors.buyerName}</p>}
            </div>

            <div>
              <Label htmlFor="buyerEmail">Email Address *</Label>
              <Input
                id="buyerEmail"
                type="email"
                value={buyerEmail}
                onChange={(e) => {
                  setBuyerEmail(e.target.value)
                  if (errors.buyerEmail) setErrors(prev => ({ ...prev, buyerEmail: '' }))
                }}
                placeholder="Enter your email address"
                required
                maxLength={255}
                className={errors.buyerEmail ? 'border-destructive' : ''}
              />
              {errors.buyerEmail && <p className="text-sm text-destructive mt-1">{errors.buyerEmail}</p>}
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="nda-agreement"
                checked={hasAgreed}
                onCheckedChange={(checked) => {
                  setHasAgreed(checked as boolean)
                  if (errors.hasAgreed) setErrors(prev => ({ ...prev, hasAgreed: '' }))
                }}
                className={errors.hasAgreed ? 'border-destructive' : ''}
              />
              <Label htmlFor="nda-agreement" className="text-sm leading-5">
                I have read and agree to the BizMatch Non-Disclosure Agreement. I understand that I am legally bound 
                to keep all confidential information shared through this platform strictly confidential for a period 
                of three (3) years. *
              </Label>
            </div>
            {errors.hasAgreed && <p className="text-sm text-destructive">{errors.hasAgreed}</p>}
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!hasAgreed || !buyerName.trim() || !buyerEmail.trim() || isSubmitting}
            >
              {isSubmitting ? "Signing..." : "Sign NDA & Access Information"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}